import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import axiosInstanceQueries from '../queriesAxios';
import { getInspectionOwnerId, getIsSharing } from '../../inspection/inspectionSelectors';
import { useUserStateStore } from '../../../zstore/userStateStore';

export interface Collaborator {
  id: string;
  email: string;
  name?: string;
  family_name?: string;
  given_name?: string;
  picture?: string;
  role: 'Owner' | 'Collaborator';
}

export interface AddCollaboratorRequest {
  emails: string[];
  inspectionId: string;
}

export interface AddCollaboratorResponse {
  success: boolean;
  message?: string;
  collaborator?: Collaborator;
}

export interface DeleteCollaboratorRequest {
  email: string;
  inspectionId: string;
}

export interface DeleteCollaboratorResponse {
  success: boolean;
  message?: string;
}

interface BackendCollaboratorResponse {
  owner: Collaborator;
  collaborators: Collaborator[];
  openToOrganization: boolean;
}

async function fetchCollaborators(inspectionId?: string): Promise<BackendCollaboratorResponse> {
  if (!inspectionId) {
    return {
      owner: { id: '', email: '', role: 'Owner' },
      collaborators: [],
      openToOrganization: false,
    };
  }
  const response = await axiosInstanceQueries.get(`/inspections/${inspectionId}/collaborators`);
  const data = response.data;

  return {
    owner: {
      ...data.owner,
      role: 'Owner' as const,
    },
    collaborators: data.collaborators.map((user: Collaborator) => ({
      ...user,
      role: 'Collaborator' as const,
    })),
    openToOrganization: data.openToOrganization,
  };
}

async function addCollaborator(request: AddCollaboratorRequest): Promise<AddCollaboratorResponse> {
  try {
    const response = await axiosInstanceQueries.post(`/inspections/${request.inspectionId}/collaborators`, {
      emails: request.emails,
    });
    return {
      success: true,
      collaborator: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to add collaborator',
    };
  }
}

async function deleteCollaborator(request: DeleteCollaboratorRequest): Promise<DeleteCollaboratorResponse> {
  try {
    await axiosInstanceQueries.delete(`/inspections/${request.inspectionId}/collaborators/${request.email}`);
    return {
      success: true,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to delete collaborator',
    };
  }
}

async function updateOrganizationAccess(
  inspectionId: string,
  openToOrganization: boolean,
): Promise<{ success: boolean; message?: string }> {
  try {
    await axiosInstanceQueries.patch(`/inspections/${inspectionId}/collaborators/organization`, {
      openToOrganization,
    });
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update organization access',
    };
  }
}

export const useCollaborators = (inspectionId?: string) => {
  const queryClient = useQueryClient();
  const inspectionOwnerId = useSelector(getInspectionOwnerId);
  const { user: currentUser } = useUserStateStore();

  const queryInfo = useQuery({
    queryKey: ['collaborators', inspectionId],
    queryFn: () => fetchCollaborators(inspectionId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!inspectionId, // Only run query when inspectionId is available
  });

  const addCollaboratorMutation = useMutation({
    mutationFn: (data: Omit<AddCollaboratorRequest, 'inspectionId'>) =>
      addCollaborator({ ...data, inspectionId: inspectionId || '' }),
    onSuccess: () => {
      // Invalidate the whole collaborators family: share state is propagated
      // across all linked inspections on the backend, so siblings' caches must
      // refetch too (not just the operated inspection).
      queryClient.invalidateQueries({ queryKey: ['collaborators'] });
    },
  });

  const deleteCollaboratorMutation = useMutation({
    mutationFn: (data: Omit<DeleteCollaboratorRequest, 'inspectionId'>) =>
      deleteCollaborator({ ...data, inspectionId: inspectionId || '' }),
    onMutate: async (deletedCollaborator) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['collaborators', inspectionId] });

      // Snapshot the previous value
      const previousCollaborators = queryClient.getQueryData(['collaborators', inspectionId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['collaborators', inspectionId], (old: BackendCollaboratorResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          collaborators: old.collaborators.filter((user) => user.email !== deletedCollaborator.email),
        };
      });

      // Return a context object with the snapshotted value
      return { previousCollaborators };
    },
    onError: (_err, _deletedCollaborator, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCollaborators) {
        queryClient.setQueryData(['collaborators', inspectionId], context.previousCollaborators);
      }
    },
    onSettled: () => {
      // Refetch the whole collaborators family (see addCollaborator note) so
      // linked siblings reflect the change, not just the operated inspection.
      queryClient.invalidateQueries({ queryKey: ['collaborators'] });
    },
  });

  const updateOrganizationAccessMutation = useMutation({
    mutationFn: (openToOrganization: boolean) => updateOrganizationAccess(inspectionId || '', openToOrganization),
    onMutate: async (newOpenToOrganization) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['collaborators', inspectionId] });

      // Snapshot the previous value
      const previousCollaborators = queryClient.getQueryData(['collaborators', inspectionId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['collaborators', inspectionId], (old: BackendCollaboratorResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          openToOrganization: newOpenToOrganization,
        };
      });

      // Return a context object with the snapshotted value
      return { previousCollaborators };
    },
    onError: (_err, _newOpenToOrganization, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCollaborators) {
        queryClient.setQueryData(['collaborators', inspectionId], context.previousCollaborators);
      }
    },
    onSettled: () => {
      // Refetch the whole collaborators family (see addCollaborator note) so
      // linked siblings reflect the change, not just the operated inspection.
      queryClient.invalidateQueries({ queryKey: ['collaborators'] });
    },
  });

  const collaborators: Collaborator[] = [
    // Add owner first
    ...(queryInfo.data?.owner ? [queryInfo.data.owner] : []),
    // Then add other collaborators
    ...(queryInfo.data?.collaborators || []),
  ];

  const isOpenToOrganization = queryInfo.data?.openToOrganization;
  const collaboratorCount = queryInfo.data?.collaborators?.length;

  const isSharing = useSelector(getIsSharing);
  const isShared = isSharing || Boolean(collaboratorCount || isOpenToOrganization);
  const isOwner = currentUser?.sub === inspectionOwnerId;
  const isCollaborator =
    !isOwner &&
    (isOpenToOrganization ||
      Boolean(
        queryInfo.data?.collaborators?.some((collaborator: Collaborator) => {
          return collaborator.email === currentUser?.email;
        }),
      ));

  return {
    ...queryInfo,
    collaborators,
    isShared,
    isCollaborator,
    isOwner,
    openToOrganization: queryInfo.data?.openToOrganization || false,
    addCollaborator: addCollaboratorMutation.mutateAsync,
    isAddingCollaborator: addCollaboratorMutation.isPending,
    deleteCollaborator: deleteCollaboratorMutation.mutateAsync,
    isDeletingCollaborator: deleteCollaboratorMutation.isPending,
    updateOrganizationAccess: updateOrganizationAccessMutation.mutateAsync,
    isUpdatingOrganizationAccess: updateOrganizationAccessMutation.isPending,
  };
};
